import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Star, Trophy } from 'lucide-react';
import ShopifyBuyButton from '@/components/ShopifyBuyButton';
import LaunchCountdown from '@/components/LaunchCountdown';
import InteractiveProductImage from './InteractiveProductImage';
import IngredientsOverview from './IngredientsOverview';
import { useLaunchStatus } from '@/hooks/useLaunchStatus';

const SalesSection = () => {
  const isLaunched = useLaunchStatus();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <Card className="bg-card border border-border shadow-lg overflow-hidden">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="bg-primary text-primary-foreground">
              <Trophy className="w-3 h-3 mr-1" />
              Norges billigste
            </Badge>
            <Badge variant="outline" className="border-green-600 text-green-600">
              <Star className="w-3 h-3 mr-1" />
              Høyeste dosering
            </Badge>
          </div>
          <CardTitle className="text-4xl font-bold mb-2 text-foreground font-orbit">
            Peveo Competition PWO-Stim-Free
          </CardTitle>
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-3xl text-muted-foreground line-through">399,-</span>
            <span className="text-5xl font-bold text-primary">299,-</span>
            <span className="text-sm text-muted-foreground">kr (inkl. mva)</span>
          </div>
          <div className="mb-4">
            <Badge variant="outline" className="border-green-600 text-green-600 bg-green-50">
              🚚 Gratis frakt over 800 kr
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xl leading-snug text-foreground font-bold">
            Betaler du for placebo?
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            De fleste pre-workouts bruker «fairy dusting» – de drysser på en ingrediens i så lav dose at den står på etiketten, men ikke gjør noe i kroppen din. Vi gjør det motsatte: kun ingredienser i maksimal klinisk dose.
          </p>

          <p className="text-xs text-muted-foreground italic mb-1">
            Basert på vitenskapelige analyser av ingrediensprofiler i bestselgende pre-workouts{' '}
            <a href="https://pubmed.ncbi.nlm.nih.gov/31083645/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">Sperlich et al. (2019)</a>;{' '}
            <a href="https://pubmed.ncbi.nlm.nih.gov/30674022/" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">Jagim et al. (2019)</a>.
            44,3 % av alle ingredienser i toppselgende PWO-er er skjult i «proprietary blends» uten oppgitt mengde.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-muted-foreground font-medium">Ingrediens</th>
                  <th className="text-center py-2 text-muted-foreground font-medium">Markedssnitt</th>
                  <th className="text-center py-2 text-primary font-bold">Peveo</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                <tr className="border-b border-border">
                  <td className="py-2">L-Citrulline</td>
                  <td className="text-center text-muted-foreground">~3 506 mg</td>
                  <td className="text-center text-primary font-bold">10 000 mg</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2">Betaine</td>
                  <td className="text-center text-muted-foreground">{'<'}2 500 mg</td>
                  <td className="text-center text-primary font-bold">5 000 mg</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-2">L-Tyrosine</td>
                  <td className="text-center text-muted-foreground">~1 227 mg</td>
                  <td className="text-center text-primary font-bold">3 000 mg</td>
                </tr>
                <tr>
                  <td className="py-2">Taurine</td>
                  <td className="text-center text-muted-foreground">~1 211 mg</td>
                  <td className="text-center text-primary font-bold">3 000 mg</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-muted p-4 rounded-lg border border-border">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="text-green-600 w-5 h-5" />
              <p className="text-foreground font-semibold">På lager - levering 1-5 dager</p>
            </div>
            <p className="text-muted-foreground text-center text-sm">
              Tar leveringen lengre? Kontakt oss og få en gratis shaker ved neste bestilling
            </p>
          </div>

          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <p className="text-primary font-semibold text-center">
              🏆 Vi skal alltid være billigst på sammenlignbare produkter - sjekk gjerne andre nettsider! Det overskuddet vi får investeres tilbake i startup-selskapet Stoad, for å lage verdens beste treningsmaskiner for muskelvekst
            </p>
          </div>

          <div className="pt-2">
            {isLaunched ? (
              <ShopifyBuyButton
                productId="9358247428327"
                componentId="product-component-sales-preworkout"
                domain="6pjebd-jw.myshopify.com"
                storefrontAccessToken="24c563b5bb4fa1b2d3b84fcb8e32090c"
                className="w-full"
              />
            ) : (
              <LaunchCountdown />
            )}
          </div>

          <div className="pt-4">
            <IngredientsOverview />
          </div>
        </CardContent>
      </Card>

      <InteractiveProductImage />
    </div>
  );
};

export default SalesSection;
